import { BookMarked, FileSpreadsheet, FileText, Info, Loader2, X } from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FileUploadZone } from '@/components/upload/FileUploadZone'

const ACCEPTED_FILE_TYPES = '.csv,.sav,.xlsx,.xlsm,.xls,.dta,.parquet'
const EXCEL_EXTENSIONS = new Set(['xlsx', 'xlsm', 'xls'])

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getFileExtension(file: File): string {
  return file.name.split('.').pop()?.toLowerCase() ?? ''
}

export interface SheetOption {
  sheetName: string
}

interface DatasetUploadCardProps {
  selectedFile: File | null
  fileError: string | null
  /** null = idle; 0-100 = upload/preflight in progress. */
  progress: number | null
  parsingSheets?: boolean
  onFilesSelected: (files: FileList) => void
  onFileClear: () => void
  /** Available sheets for Excel files (populated client-side or after backend analysis). */
  sheetOptions?: SheetOption[]
  audienceSheet?: string
  dictionarySheet?: string | null
  onAudienceSheetChange?: (value: string) => void
  onDictionarySheetChange?: (value: string | null) => void
  className?: string
}

export function DatasetUploadCard({
  selectedFile,
  fileError,
  progress,
  parsingSheets,
  onFilesSelected,
  onFileClear,
  sheetOptions,
  audienceSheet,
  dictionarySheet,
  onAudienceSheetChange,
  onDictionarySheetChange,
  className,
}: DatasetUploadCardProps) {
  const { t } = useTranslation('app')
  const loading = progress !== null
  const hasFile = selectedFile && !fileError
  const isExcel = hasFile && EXCEL_EXTENSIONS.has(getFileExtension(selectedFile))
  const hasSheets = isExcel && sheetOptions && sheetOptions.length > 0

  return (
    <div
      className={cn(
        'flex flex-col gap-5 rounded-xl px-6 py-7',
        hasFile ? 'bg-card/50 dark:bg-card' : 'border-dashed-6',
        className,
      )}
    >

      {/* Section header */}
      <div className="flex items-center gap-1">
        <p className="text-lg font-medium leading-[1.4] text-foreground">
          {t('audienceCreate.datasetTitle')}
        </p>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex text-muted-foreground hover:text-foreground transition-colors"
                aria-label={t('audienceCreate.datasetInfoAria')}
              >
                <Info className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-left leading-relaxed">
              {t('audienceCreate.datasetInfo')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {hasFile && loading && !hasSheets ? (
        /* ---- Upload / preflight with determinate progress bar ---- */
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-card-inset">
            <FileText className="h-4 w-4 text-primary dark:text-muted-foreground" />
          </div>
          <div className="flex flex-1 flex-col gap-1 min-w-0 pr-2">
            <div className="flex items-baseline justify-between">
              <p className="truncate text-base font-medium leading-[1.6] text-foreground">
                {selectedFile.name}
              </p>
              <p className="shrink-0 text-base leading-[1.6] text-foreground ml-2">
                {progress}%
              </p>
            </div>
            <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted dark:bg-background">
              <div
                className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-l from-primary to-accent-highlight"
                style={{
                  width: `${progress}%`,
                  transition: 'width 300ms ease',
                }}
              />
            </div>
            <p className="text-sm leading-[1.5] text-muted-foreground">
              {progress! < 20 ? t('audienceCreate.uploading') : t('audienceCreate.analyzing')}
            </p>
          </div>
          <button
            type="button"
            onClick={onFileClear}
            className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t('audienceCreate.cancelUpload')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : hasFile ? (
        /* ---- File attached ---- */
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-card-inset">
            <FileText className="h-4 w-4 text-primary dark:text-muted-foreground" />
          </div>
          <div className="flex flex-1 flex-col min-w-0">
            <p className="truncate text-base font-medium leading-[1.6] text-foreground">
              {selectedFile.name}
            </p>
            <p className="text-sm leading-[1.5] text-muted-foreground">
              {formatFileSize(selectedFile.size)}
            </p>
          </div>
          <button
            type="button"
            onClick={onFileClear}
            className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t('audienceCreate.removeFile')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        /* ---- Drop zone ---- */
        <FileUploadZone
          variant="dataset"
          accept={ACCEPTED_FILE_TYPES}
          disabled={loading}
          onFilesSelected={onFilesSelected}
        />
      )}

      {/* File error */}
      {fileError && (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <X className="h-4 w-4 shrink-0" />
          <span>{fileError}</span>
        </div>
      )}

      {/* ---- Reading sheets indicator ---- */}
      {isExcel && parsingSheets && !hasSheets && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>{t('audienceCreate.readingSheets')}</span>
        </div>
      )}

      {/* ---- Inline sheet picker (Excel files with known sheets) ---- */}
      {hasSheets && (
        <>
          <div className="h-px w-full bg-border" />
          <div className="flex flex-col gap-3">
            <p className="text-base font-medium leading-[1.6] text-foreground">
              {t('audienceCreate.multiSheet')}
            </p>

            {/* Data sheet */}
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-2">
                <FileSpreadsheet className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
                <div className="flex flex-col">
                  <p className="text-base font-medium leading-[1.6] text-foreground">
                    {t('audienceCreate.dataSheet')}
                  </p>
                  <p className="text-sm leading-[1.5] text-muted-foreground">
                    {t('audienceCreate.dataSheetHint')}
                  </p>
                </div>
              </div>
              <Select
                value={audienceSheet}
                onValueChange={onAudienceSheetChange}
              >
                <SelectTrigger className="h-[38px] w-[351px] rounded-md border-border bg-transparent text-sm text-foreground">
                  <SelectValue placeholder={t('audienceCreate.chooseSheet')} />
                </SelectTrigger>
                <SelectContent>
                  {sheetOptions.map((opt) => (
                    <SelectItem key={opt.sheetName} value={opt.sheetName} className="focus:bg-card-inset">
                      {opt.sheetName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dictionary sheet */}
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-2">
                <BookMarked className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
                <div className="flex flex-col">
                  <p className="text-base font-medium leading-[1.6] text-foreground">
                    {t('audienceCreate.dictionarySheet')}
                  </p>
                  <p className="text-sm leading-[1.5] text-muted-foreground">
                    {t('audienceCreate.dictionaryHint')}
                  </p>
                </div>
              </div>
              <Select
                value={dictionarySheet ?? '__none__'}
                onValueChange={(v) =>
                  onDictionarySheetChange?.(v === '__none__' ? null : v)
                }
              >
                <SelectTrigger className="h-[38px] w-[351px] rounded-md border-border bg-transparent text-sm text-foreground">
                  <SelectValue placeholder={t('audienceCreate.none')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" className="focus:bg-card-inset">{t('audienceCreate.none')}</SelectItem>
                  {sheetOptions.map((opt) => (
                    <SelectItem key={opt.sheetName} value={opt.sheetName} className="focus:bg-card-inset">
                      {opt.sheetName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}

      {/* ---- Upload progress below sheet picker (Excel multi-sheet) ---- */}
      {hasFile && loading && hasSheets && (
        <>
          <div className="h-px w-full bg-border" />
          <div className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between">
              <p className="text-sm font-medium leading-[1.5] text-muted-foreground">
                {progress! < 20 ? t('audienceCreate.uploading') : t('audienceCreate.analyzing')}
              </p>
              <p className="text-sm leading-[1.5] text-muted-foreground">
                {progress}%
              </p>
            </div>
            <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted dark:bg-background">
              <div
                className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-l from-primary to-accent-highlight"
                style={{
                  width: `${progress}%`,
                  transition: 'width 300ms ease',
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
